import anime from 'animejs';
import { live } from '../data/live';
import { flagGeometry } from './flag-geometry';
import { flagRenderer } from './flag-renderer';

/**
 * The green "INCENTIVI" panel: one incentive at a time, 20 s each, fixed goals first and
 * then bid wars, in the order live.parseIncentives() produced them.
 *
 * Two shapes share the panel. A fixed bid gets a progress bar counting to
 * `amount_total / goal`; a bid war gets up to three ranked bars. The bar heights are
 * fixed at 100 / 85 / 75 % of the pop-up travel — they rank the options, they do not
 * scale to the amounts.
 */
export const incentiveFlag = {
    incentiveVisibleTime: 20000,
    fixedIncentivePercent: 0,

    // Each panel resolves its own deferred when it leaves the screen. The driver owns
    // what comes next; a panel calling its successor is what created the
    // infobar → overlay-renderer → flag-renderer → infobar import cycle.
    greenFinished: undefined,
    playGreen: () => {
        const done = new Promise((resolve) => { incentiveFlag.greenFinished = resolve; });
        incentiveFlag.showGreen();
        return done;
    },

    showGreen: () => {
        if (live.incentives.length == 0) {
            incentiveFlag.greenFinished?.();
            return;
        }

        anime({
            targets: ['#flag-piece-green>.long-flag', '#flag-piece-white>.long-flag', '#flag-piece-red>.long-flag'],
            translateX: flagGeometry.flagOutExtent,
            skew: flagGeometry.skewProperties,
            easing: 'easeOutQuint',
            duration: flagGeometry.linearOutDuration,
            complete: incentiveFlag.showGreenContents
        });
    },

    showGreenContents: () => {
        if (live.incentives.length == 0) {
            incentiveFlag.greenFinished?.();
        } else {
            anime({
                targets: '#arrow-incentive',
                translateX: 80,
                opacity: 1,
                skew: flagGeometry.skewPropertiesNegative,
                easing: 'linear',
                duration: flagGeometry.futureRunAnimationDuration,
                complete: incentiveFlag.startIncentivesLoop,
            });
        }
    },

    startIncentivesLoop: () => {
        live.incentives.forEach(function (incentive, i) {
            setTimeout(function () {
                incentiveFlag.populateAndShowIncentiveData(incentive);
            }, incentiveFlag.incentiveVisibleTime * i);
        });
        setTimeout(function () {
            incentiveFlag.hideGreenContents();
        }, incentiveFlag.incentiveVisibleTime * (live.incentives.length));
    },

    hideGreenContents: () => {
        anime({
            targets: '#arrow-incentive',
            translateX: 100,
            opacity: 0,
            skew: flagGeometry.skewPropertiesNegative,
            easing: 'linear',
            duration: flagGeometry.futureRunAnimationDuration,
            complete: () => incentiveFlag.greenFinished?.(),
        });
    },

    populateAndShowIncentiveData: (incentive) => {
        incentiveFlag.showIncentiveContainer(incentive.type);
        if (incentive.type === 'option')
            incentiveFlag.populateAndShowOptionIncentiveData(incentive);
        else if (incentive.type === 'fixed')
            incentiveFlag.populateAndShowFixedIncentiveData(incentive);
    },

    showIncentiveContainer: (type) => {
        const fixedIncentiveContainerElement = document.querySelector('.fixed-data');
        const optionIncentiveContainerElement = document.querySelector('.bar-data');
        const isFixed = (type === 'fixed');

        fixedIncentiveContainerElement.style.display = isFixed ? 'flex' : 'none';
        optionIncentiveContainerElement.style.display = isFixed ? 'none' : 'flex';
    },

    showIncentiveGameData: (incentive) => {
        const incentiveElement = document.querySelector('.incentive-data');
        const incentiveGameNameElement = incentiveElement.querySelector('.game-name');
        const incentiveGameCategoryElement = incentiveElement.querySelector('.game-category');

        incentiveGameNameElement.innerHTML = incentive.gameName;
        incentiveGameCategoryElement.innerHTML = incentive.name;

        anime({
            targets: '.incentive-data .game-data',
            translateX: 0,
            opacity: 1,
            easing: 'easeOutSine',
            duration: flagGeometry.futureRunAnimationDuration,
        });

        setTimeout(function () {
            anime({
                targets: '.incentive-data .game-data',
                translateX: 20,
                opacity: 0,
                easing: 'easeOutSine',
                duration: flagGeometry.futureRunAnimationDuration,
                complete: function () {
                    anime({
                        targets: '.incentive-data .game-data',
                        translateX: -20,
                        opacity: 0,
                        easing: 'steps(1)',
                        duration: flagGeometry.futureRunAnimationDuration,
                    });
                }
            });
        }, incentiveFlag.incentiveVisibleTime - (flagGeometry.futureRunAnimationDuration * 3));
    },

    populateAndShowFixedIncentiveData: (incentive) => {
        if (incentive.type !== 'fixed') return;

        incentiveFlag.showIncentiveGameData(incentive);

        let formattedTotal = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(incentive.total ?? 0);
        let formattedGoal = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(incentive.goal ?? 0);

        console.log(incentive, formattedTotal, formattedGoal);
        const donationTotalsNumberElement = document.querySelector('.incentive-data .fixed-data .donation-totals-number');
        donationTotalsNumberElement.innerHTML = formattedTotal + " / " + formattedGoal;
        const percent = incentive.total * 100 / incentive.goal;
        console.log(incentive.total * 100 / incentive.goal, percent);
        incentiveFlag.moveFixedIncentiveBar(percent);
    },

    moveFixedIncentiveBar: (percent) => {
        if (percent > 100) percent = 100;
        else if (percent < 0) percent = 0;

        const minMargin = -705;
        const maxMargin = 0;
        const marginDelta = minMargin - maxMargin;
        const marginPercent = marginDelta * percent / 100;
        const margin = minMargin - marginPercent;

        incentiveFlag.fixedIncentivePercent = 0;
        const percentElement = document.querySelector('.incentive-data .fixed-data .donation-bar-progressbar .percent');
        const hideDelay = incentiveFlag.incentiveVisibleTime - 1300;
        const duration = flagGeometry.futureRunAnimationDuration * 4;
        percentElement.innerHTML = "0%";
        anime({
            targets: '.incentive-data .fixed-data .donation-bar-progressbar',
            translateX: [
                { value: minMargin, duration: 10 },
            ],
            easing: 'steps(1)',
            complete: function () {
                anime({
                    targets: '.incentive-data .fixed-data .donation-totals',
                    translateY: [
                        { value: 0 },
                        { value: -35, delay: hideDelay }
                    ],
                    opacity: [
                        { value: 1 },
                        { value: 0, delay: hideDelay }
                    ],
                    easing: 'easeOutQuint',
                    duration: duration,
                });
                anime({
                    targets: '.incentive-data .fixed-data .donation-bar-container',
                    translateY: [
                        { value: 0 },
                        { value: 40, delay: hideDelay }
                    ],
                    opacity: [
                        { value: 1 },
                        { value: 0, delay: hideDelay }
                    ],
                    easing: 'easeOutQuint',
                    duration: duration,
                });
                anime({
                    targets: '.incentive-data .fixed-data .donation-bar-progressbar',
                    translateX: [
                        { value: margin, duration: percent * 50 },
                    ],
                    easing: 'easeOutCirc',
                });
                anime({
                    targets: incentiveFlag,
                    fixedIncentivePercent: percent,
                    easing: 'easeOutCirc',
                    duration: percent * 50,
                    update: function () {
                        percentElement.innerHTML = parseInt(incentiveFlag.fixedIncentivePercent) + '%';
                    },
                })
            }
        });
    },

    populateAndShowOptionIncentiveData: (incentive) => {
        if (incentive.type !== 'option') return;

        incentiveFlag.showIncentiveGameData(incentive);

        const noOptionsElement = document.querySelector('.incentive-data .bar-data .no-options');

        let options = incentive.options;
        if (options.length === 0) {
            noOptionsElement.style.display = 'flex';
            setTimeout(function () {
                anime({
                    targets: noOptionsElement,
                    easing: 'easeOutQuint',
                    opacity: [
                        { value: 1 },
                        { value: 0, delay: incentiveFlag.incentiveVisibleTime - 1000 },
                    ],
                    translateX: [
                        { value: 0 },
                        { value: 40, delay: incentiveFlag.incentiveVisibleTime - 1000 },
                        { value: -40, delay: flagGeometry.futureRunAnimationDuration },
                    ],
                    duration: flagGeometry.futureRunAnimationDuration * 3,
                }, 1000);
            });
        } else {
            noOptionsElement.style.display = 'none';
            const barPopupWait = 80;
            const percents = [100, 85, 75];
            const allEmpty = options.filter(o => o.total == 0).length === options.length;
            for (let i in options) {
                i = Number(i);
                if (i >= 3)
                    break;

                let option = options[i];
                let indexFromOne = i + 1;
                incentiveFlag.populateOptionIncentiveBarData(indexFromOne, option)
                setTimeout(function () {
                    incentiveFlag.showOptionIncentiveBar(indexFromOne, allEmpty ? 75 : percents[i]);
                }, barPopupWait * indexFromOne);
            }
        }
    },

    populateOptionIncentiveBarData: (i, childIncentive) => {
        const incentiveElement = document.querySelector('.incentive-data');
        const incentiveBarElement = incentiveElement.querySelector('.bar-' + i);
        const incentiveGameNameElement = incentiveBarElement.querySelector('.bar-name');
        const incentiveGameTotalElement = incentiveBarElement.querySelector('.bar-total');

        incentiveGameNameElement.innerHTML = childIncentive.name;
        let formattedNumber = new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(childIncentive.total);
        incentiveGameTotalElement.innerHTML = formattedNumber;
    },

    showOptionIncentiveBar: (i, percent) => {
        const minMargin = -90;
        const maxMargin = -15;
        const marginDelta = minMargin - maxMargin;
        const marginPercent = marginDelta * percent / 100;
        const margin = minMargin - marginPercent;

        anime({
            targets: '.incentive-data .bar-' + i,
            marginBottom: margin,
            easing: 'easeOutSine',
            duration: flagGeometry.futureRunAnimationDuration,
            delay: flagGeometry.futureRunAnimationDuration,
            complete: function () {
                setTimeout(function () {
                    anime({
                        targets: '.incentive-data .bar-' + i,
                        marginBottom: minMargin,
                        easing: 'easeOutSine',
                        duration: flagGeometry.futureRunAnimationDuration,
                    });
                }, incentiveFlag.incentiveVisibleTime - 1000);
            }
        });
    },

};

window.incentiveFlag = incentiveFlag;
